import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useWallets() {
  const [wallets, setWallets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { send, isConnected, isOpen, connect, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);

  const fetchWallets = useCallback(
    async (attempt = 0) => {
      if (!mountedRef.current) return;

      setIsLoading(true);
      setError("");

      try {
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }

        if (!isConnected) {
          await connect();
          await new Promise((resolve, reject) => {
            const start = Date.now();
            const intv = setInterval(() => {
              if (!mountedRef.current) {
                clearInterval(intv);
                reject(new Error("Component unmounted"));
              } else if (isOpen()) {
                clearInterval(intv);
                resolve();
              } else if (Date.now() - start > 5000) {
                clearInterval(intv);
                reject(new Error("Connection timeout"));
              }
            }, 100);
          });
        }

        const group_id = localStorage.getItem("group_id") || "1";

        // ✅ use request event, not response
        const response = await send("cms_get_wallets", { group_id });
        console.log("Wallets response:", response);

        if (!response || response.type !== "cms_get_wallets_response") {
          throw new Error("Invalid response from server");
        }

        if (!Array.isArray(response.data)) {
          throw new Error("No data available. Try refreshing the page");
        }

        // 🔄 normalise camelCase keys to snake_case for UI
        const normalised = response.data.map((w) => ({
          wallet_id: w.walletID,
          user_id: w.userID,
          balance: w.balance,
          currency: w.currency,
          created_at: w.createdAt,
          modified_at: w.modifiedAt,
        }));

        setWallets(normalised);
      } catch (err) {
        setError(err.message);
        if (mountedRef.current) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
          retryTimeoutRef.current = setTimeout(
            () => fetchWallets(attempt + 1),
            delay
          );
        }
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, isOpen, connect, send]
  );

  useEffect(() => {
    mountedRef.current = true;
    fetchWallets();

    const unregister = registerHandler("wallets_update_group_1", (msg) => {
      if (Array.isArray(msg.data) && mountedRef.current) {
        const normalised = msg.data.map((w) => ({
          wallet_id: w.walletID,
          user_id: w.userID,
          balance: w.balance,
          currency: w.currency,
          created_at: w.createdAt,
          modified_at: w.modifiedAt,
        }));
        setWallets(normalised);
      }
    });

    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      unregister();
    };
  }, [fetchWallets, registerHandler]);

  return { wallets, isLoading, error, refetch: () => fetchWallets(0) };
}
